import { useCallback, useEffect, useRef, useState } from 'react';

const THRESHOLD = 80;
const MAX_PULL = 120;

const usePullToRefresh = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const isAtTop = () => window.scrollY <= 0;

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (isAtTop() && !refreshing) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    },
    [refreshing]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pulling.current || refreshing) return;
      if (!isAtTop()) {
        pulling.current = false;
        setPullDistance(0);
        return;
      }

      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) {
        // Rubber-band effect: diminishing returns past threshold
        const distance = Math.min(delta * 0.5, MAX_PULL);
        setPullDistance(distance);
        if (distance > 10) {
          e.preventDefault();
        }
      }
    },
    [refreshing]
  );

  const handleTouchEnd = useCallback(() => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      // Small delay so spinner is visible
      setTimeout(() => {
        window.location.reload();
      }, 400);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance]);

  useEffect(() => {
    // Only enable on touch devices
    const isTouchDevice = 'ontouchstart' in window;
    if (!isTouchDevice) return undefined;

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pullDistance, refreshing };
};

export default usePullToRefresh;
